import { Trans, useTranslation } from 'react-i18next';
import { Link, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { EMlpIconTypes, EMplVariantTypes, MplIconMessage } from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../../hooks';
import { TInfoMessageConfig } from '../hooks/types';

type TDTicketInfoMessageHeaderSectionProps = NonNullable<TInfoMessageConfig>;

const transComponents = {
	b: <Typography variant="b2-m" component="span" color="primary.main" />,
};

export const DTicketInfoMessageHeaderSection = (
	props: TDTicketInfoMessageHeaderSectionProps
) => {
	const { t } = useTranslation('userDTicket');
	const { navigateToSubscriptionEditPause, navigateToSubscriptionEdit } =
		usePassengerAppNavigate();

	const renderMessageContent = () => {
		switch (props.type) {
			case 'pause-scheduled':
				return (
					<Typography
						variant="b2"
						color="primary.main"
						data-testid="dticket-infoMessageHeader-pauseScheduled-text"
					>
						<Trans
							t={t}
							i18nKey="info_message_header.pause_scheduled.text"
							values={{ date: dayjs(props.pauseStart).format('DD.MM.YYYY') }}
							components={transComponents}
						/>
						{props.showActionLink && (
							<>
								<br />
								<Link
									component="button"
									onClick={() => navigateToSubscriptionEditPause()}
									underline="always"
									color="primary.main"
									data-testid="dticketScreen-infoMessageHeaderSection-editPause-button"
								>
									{t('info_message_header.pause_scheduled.link')}
								</Link>
							</>
						)}
					</Typography>
				);

			case 'cancellation-scheduled':
				return (
					<Typography
						variant="b2"
						color="primary.main"
						data-testid="dticket-infoMessageHeader-cancellationScheduled-text"
					>
						<Trans
							t={t}
							i18nKey="info_message_header.cancellation_scheduled.text"
							values={{ date: dayjs(props.date).format('DD.MM.YYYY') }}
							components={transComponents}
						/>
						{props.showActionLink && (
							<>
								<br />
								<Link
									component="button"
									onClick={() => navigateToSubscriptionEdit()}
									underline="always"
									color="primary.main"
									data-testid="dticketScreen-infoMessageHeaderSection-editCancellation-button"
								>
									{t('info_message_header.cancellation_scheduled.link')}
								</Link>
							</>
						)}
					</Typography>
				);

			case 'paused':
				return (
					<Typography
						variant="b2"
						color="primary.main"
						data-testid="dticket-infoMessageHeader-paused-text"
					>
						<Trans
							t={t}
							i18nKey="info_message_header.paused.text"
							values={{ date: dayjs(props.date).format('DD.MM.YYYY') }}
							components={transComponents}
						/>
					</Typography>
				);

			case 'paused-with-cancellation-scheduled':
				return (
					<Typography
						variant="b2"
						color="primary.main"
						data-testid="dticket-infoMessageHeader-pausedWithCancellationScheduled-text"
					>
						<Trans
							t={t}
							i18nKey="info_message_header.paused_with_cancellation_scheduled.text"
							values={{
								pauseEnd: dayjs(props.pauseEnd).format('DD.MM.YYYY'),
								canceledAt: dayjs(props.canceledAt).format('DD.MM.YYYY'),
							}}
							components={transComponents}
						/>
					</Typography>
				);

			case 'paused-with-cancellation-scheduled-same-date':
				return (
					<Typography
						variant="b2"
						color="primary.main"
						data-testid="dticket-infoMessageHeader-pausedWithCancellationScheduledSameDate-text"
					>
						<Trans
							t={t}
							i18nKey="info_message_header.paused_with_cancellation_scheduled_same_date.text"
							values={{
								canceledAt: dayjs(props.canceledAt).format('DD.MM.YYYY'),
							}}
							components={transComponents}
						/>
					</Typography>
				);

			case 'pause-scheduled-with-cancellation-scheduled':
				return (
					<Typography
						variant="b2"
						color="primary.main"
						data-testid="dticket-infoMessageHeader-pauseScheduledWithCancellationScheduled-text"
					>
						<Trans
							t={t}
							i18nKey="info_message_header.pause_scheduled_with_cancellation_scheduled.text"
							values={{
								pauseStart: dayjs(props.pauseStart).format('DD.MM.YYYY'),
								canceledAt: dayjs(props.canceledAt).format('DD.MM.YYYY'),
							}}
							components={transComponents}
						/>
					</Typography>
				);
			default:
				return null;
		}
	};

	return (
		<MplIconMessage
			type={EMlpIconTypes.Info}
			variant={EMplVariantTypes.Info}
			message={renderMessageContent()}
			dataTestid="dticket-infoMessageHeader-iconMessage"
		/>
	);
};
